import { isEmpty } from 'lodash';
import React, { Fragment } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';
import { OrderCategory } from "../../../api/endpoints/Order";
import Col from '../../../components/Common/Col';
import Row from '../../../components/Common/Row';
import LoadingIndicator from "../../../components/Feedback/LoadingIndicator";
import { MailLink } from '../../../components/Navigation/Links/ExternalLink';
import OrderName from "../../../components/OrderName";
import OrderPDFButton from "../../../components/OrderPDFButton";
import OrderSubmissionForm from "../../../components/OrderSubmissionForm";
import OrderSubmissionFormCompatibility from "../../../components/OrderSubmissionFormCompatibility";
import { UserType } from '../../../entities/User';
import useOrderCostingVersion from "../../../hooks/useOrderCostingVersion";
import OrderPolicy from '../../../policies/OrderPolicy';
import DateTime from "../../../utils/DateTime";
import OrderHelpers from "../../../utils/OrderHelpers";
import OrderExpirationAlert from "../ExpirationAlert";
import LicenceDetails from "../LicenceDetails";
import OrderCostSummary from "../OrderCostSummary";
import WebOrderDetails from '../OrderDetails/WebOrderDetails';
import OrderHeader from '../OrderHeader';
import { OrderProps } from '../types';

const WebOrder = (props: OrderProps<OrderCategory.WEB>) => {
  const {
    user,
    order,
    sets,
    chosenProvider,
    onSubmitClick,
    submissionDialogOpen,
    setSubmissionDialogOpen,
    onSubmissionComplete,
    onApprove,
    onReject,
    onCancel
  } = props;
  const { orderCostingVersions, currentOrderCostingVersion } = useOrderCostingVersion(order);
  const isOrderExpired = OrderHelpers.isExpired(order);

  const hasCostError = Boolean(order.cost.data && order.cost.data.error);

  if (!orderCostingVersions) {
    return <LoadingIndicator />;
  }

  return (
    <Fragment>
      {
        isOrderExpired &&
        <OrderExpirationAlert />
      }

      <OrderHeader order={order} />

      <Row className="margin-bottom-xs">
        <Col>
          <strong>Order name</strong>: <OrderName order={order} />
        </Col>
      </Row>

      {
        user.type !== UserType.Customer &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Customer:</strong> {order.customer}
            &nbsp;(<MailLink email={order.email} />)
          </Col>
        </Row>
      }

      {
        order.start_date &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Start date:</strong> {DateTime.Format.dateLong(order.start_date)}
          </Col>
        </Row>
      }

      {
        chosenProvider &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Licensor:</strong> {chosenProvider.name}
          </Col>
        </Row>
      }

      <LicenceDetails order={order} user={user} />

      <Card className="margin-bottom-sm">
        <Card.Body>
          <WebOrderDetails />

          {
            !isEmpty(order.cost) &&
            <OrderCostSummary order={order} />
          }
        </Card.Body>
      </Card>

      <Row>
        <Col>
          <div style={{ display: 'flex' }}>
            {
              OrderPolicy.canApprove(user, order) &&
              <Fragment>
                <Button
                  onClick={onApprove}
                  style={{ marginRight: '2em' }}
                  variant="success"
                >
                  Approve order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canReject(user, order) &&
              <Fragment>
                <Button
                  onClick={onReject}
                  style={{ marginRight: '2em' }}
                  variant="danger"
                >
                  Reject order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canCancel(user, order) &&
              <Fragment>
                <Button
                  onClick={onCancel}
                  style={{ marginRight: '2em' }}
                  variant="danger"
                >
                  Cancel order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canSubmit(user, order, orderCostingVersions) &&
              <Fragment>
                <Button
                  onClick={onSubmitClick}
                  style={{ marginRight: '2em' }}
                  disabled={hasCostError}
                >
                  Submit order
                </Button>
              </Fragment>
            }

            <Modal size="lg" show={submissionDialogOpen} onHide={() => setSubmissionDialogOpen(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Submit order</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <OrderSubmissionFormCompatibility order={order}>
                  {
                    currentOrderCostingVersion &&
                    <OrderSubmissionForm
                      order={order}
                      orderCostingVersion={currentOrderCostingVersion}
                      onComplete={onSubmissionComplete}
                    />
                  }
                </OrderSubmissionFormCompatibility>
              </Modal.Body>
            </Modal>

            <OrderPDFButton
              order={order}
              currentCostingVersion={currentOrderCostingVersion}
              sets={sets}
              provider={chosenProvider}
              disabled={hasCostError}
            />
          </div>
        </Col>
      </Row>
    </Fragment>
  );
}

export default WebOrder;
